import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl, { Marker } from 'maplibre-gl';
import { useMapStore } from '@/stores/mapStore';
import { useIsochroneStore, type IsochroneProfile } from './isochroneStore';
import { routingService, type IsochroneContour } from '@/services/routingService';

// "Reachable area" (isochrone) explorer. Given an origin, a travel mode and one
// or more time budgets, it asks the routing-service /isochrone endpoint for the
// reachability contours and draws them as translucent nested fill polygons
// (outer = lighter) with per-budget colored outlines and a legend. Origin is
// picked by clicking the map (handled in MapContainer's click guard, which reads
// isochroneStore) or via the user's current location.
//
// Follows the TrafficLayer pattern: a sibling tiny store holds the shared flag,
// this single component owns both the MapLibre source/layers and the panel UI,
// and every source/layer/marker is torn down on exit or unmount (no leaks).

const SOURCE_ID = 'isochrone-src';
const FILL_PREFIX = 'isochrone-fill-';
const LINE_PREFIX = 'isochrone-line-';

type Status = 'idle' | 'loading' | 'ok' | 'empty' | 'error';

// Distinct color per time budget, ordered so the smallest budget (closest reach)
// reads as the warmest/most saturated and larger budgets get cooler/lighter — an
// index into this by the budget's rank among the selected times.
const CONTOUR_COLORS = ['#d93025', '#f9640a', '#f9ab00', '#34a853', '#1a73e8', '#7048e8'];

function colorForRank(rank: number): string {
  return CONTOUR_COLORS[rank % CONTOUR_COLORS.length];
}

// A valid polygon ring needs at least a closed triangle (>= 4 positions). The
// backend returns an empty ring when a budget produced no reachable points.
function isValidContour(c: IsochroneContour): boolean {
  const ring = c.geometry?.coordinates?.[0];
  return Array.isArray(ring) && ring.length >= 4;
}

function createOriginElement(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'isochrone-origin-marker';
  el.innerHTML = `
    <div class="w-6 h-6 drop-shadow-md">
      <svg viewBox="0 0 24 24" width="24" height="24">
        <circle cx="12" cy="12" r="8" fill="#1a73e8" stroke="white" stroke-width="2.5"/>
        <circle cx="12" cy="12" r="2.5" fill="white"/>
      </svg>
    </div>
  `;
  return el;
}

export function IsochroneLayer() {
  const { map, isLoaded } = useMapStore();
  const userLocationState = useMapStore((s) => s.userLocationState);

  const active = useIsochroneStore((s) => s.active);
  const picking = useIsochroneStore((s) => s.picking);
  const origin = useIsochroneStore((s) => s.origin);
  const profile = useIsochroneStore((s) => s.profile);
  const times = useIsochroneStore((s) => s.times);
  const {
    setActive,
    setPicking,
    setOrigin,
    setProfile,
    toggleTime,
    setTimes,
    reset,
  } = useIsochroneStore();

  const [status, setStatus] = useState<Status>('idle');
  // Ranked time budgets that were actually drawn (drives the legend so it never
  // shows a color for a budget the backend couldn't return).
  const [drawn, setDrawn] = useState<{ time: number; color: string }[]>([]);
  const [customMin, setCustomMin] = useState('');

  const markerRef = useRef<Marker | null>(null);
  const reqIdRef = useRef(0);
  // Layer ids we created, so cleanup and style-reload teardown are exact.
  const layerIdsRef = useRef<Set<string>>(new Set());
  // Last drawn feature collection + rank order, replayed after a base-style
  // switch (setStyle drops custom sources/layers).
  const lastDataRef = useRef<{
    fc: GeoJSON.FeatureCollection;
    ranks: { time: number; color: string }[];
  } | null>(null);

  // --- MapLibre source/layer helpers -----------------------------------------

  const removeLayers = useCallback(() => {
    if (!map) return;
    layerIdsRef.current.forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    layerIdsRef.current.clear();
  }, [map]);

  const clearMap = useCallback(() => {
    removeLayers();
    if (map && map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    lastDataRef.current = null;
  }, [map, removeLayers]);

  // Paint the stored feature collection. Adds fill+outline layers largest-budget
  // first so smaller (nearer) rings render on top; per-budget opacity keeps the
  // outer rings lighter.
  const paint = useCallback(
    (fc: GeoJSON.FeatureCollection, ranks: { time: number; color: string }[]) => {
      if (!map) return;
      const existing = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(fc);
      } else {
        map.addSource(SOURCE_ID, { type: 'geojson', data: fc });
      }
      removeLayers();

      const n = ranks.length;
      // Iterate largest budget -> smallest so smaller rings are added last (top).
      for (let rank = n - 1; rank >= 0; rank--) {
        const { time, color } = ranks[rank];
        // Outer (higher rank) lighter, inner more solid.
        const fillOpacity = 0.36 - rank * (0.12 / Math.max(1, n - 1));
        const fillId = `${FILL_PREFIX}${time}`;
        const lineId = `${LINE_PREFIX}${time}`;
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: SOURCE_ID,
          filter: ['==', ['get', 'time'], time],
          paint: { 'fill-color': color, 'fill-opacity': Math.max(0.12, fillOpacity) },
        });
        map.addLayer({
          id: lineId,
          type: 'line',
          source: SOURCE_ID,
          filter: ['==', ['get', 'time'], time],
          paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 },
        });
        layerIdsRef.current.add(fillId);
        layerIdsRef.current.add(lineId);
      }
    },
    [map, removeLayers]
  );

  // --- Fetch + draw on origin/profile/times change ---------------------------

  useEffect(() => {
    if (!map || !isLoaded || !active) return;

    // Keep the origin marker in sync whenever there is an origin.
    if (origin) {
      if (!markerRef.current) {
        markerRef.current = new Marker({ element: createOriginElement(), anchor: 'center' })
          .setLngLat([origin.lng, origin.lat])
          .addTo(map);
      } else {
        markerRef.current.setLngLat([origin.lng, origin.lat]);
      }
    }

    if (!origin || times.length === 0) {
      // No origin -> also drop the origin marker (e.g. after Clear/reset).
      if (!origin && markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      removeLayers();
      if (map.getSource(SOURCE_ID)) {
        (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
      lastDataRef.current = null;
      setDrawn([]);
      setStatus('idle');
      return;
    }

    let cancelled = false;
    const myId = ++reqIdRef.current;
    setStatus('loading');

    routingService
      .getIsochrone(origin, times, profile)
      .then((result) => {
        if (cancelled || myId !== reqIdRef.current) return;

        const valid = (result.contours || []).filter(isValidContour);
        if (valid.length === 0) {
          removeLayers();
          lastDataRef.current = null;
          setDrawn([]);
          setStatus('empty');
          return;
        }

        // Rank by ascending time so color/opacity assignment is stable.
        const sorted = [...valid].sort((a, b) => a.time - b.time);
        const ranks = sorted.map((c, i) => ({ time: c.time, color: colorForRank(i) }));

        const fc: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: sorted.map((c) => ({
            type: 'Feature',
            geometry: c.geometry,
            properties: { time: c.time, distance: c.distance ?? null },
          })),
        };

        lastDataRef.current = { fc, ranks };
        paint(fc, ranks);
        setDrawn(ranks);
        setStatus('ok');
      })
      .catch(() => {
        if (cancelled || myId !== reqIdRef.current) return;
        removeLayers();
        lastDataRef.current = null;
        setDrawn([]);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [map, isLoaded, active, origin, profile, times, paint, removeLayers]);

  // Re-apply after a base-style switch (setStyle drops custom sources/layers).
  useEffect(() => {
    if (!map || !active) return;
    const onStyleLoad = () => {
      if (lastDataRef.current) {
        paint(lastDataRef.current.fc, lastDataRef.current.ranks);
      }
    };
    map.on('style.load', onStyleLoad);
    return () => {
      map.off('style.load', onStyleLoad);
    };
  }, [map, active, paint]);

  // Full teardown when the mode is turned off or the component unmounts.
  useEffect(() => {
    if (active) return;
    reqIdRef.current++; // invalidate any in-flight fetch
    clearMap();
    setStatus('idle');
    setDrawn([]);
  }, [active, clearMap]);

  useEffect(() => {
    // Unmount safety net (map may already be gone; clearMap guards on map).
    return () => {
      reqIdRef.current++;
      clearMap();
    };
  }, [clearMap]);

  // --- Panel handlers --------------------------------------------------------

  const handleUseMyLocation = useCallback(() => {
    if (userLocationState?.coordinates) {
      setOrigin(userLocationState.coordinates);
      return;
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {/* silently ignore; user can still click the map */},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [userLocationState, setOrigin]);

  const handleAddCustom = useCallback(() => {
    const v = parseInt(customMin, 10);
    if (!Number.isFinite(v) || v <= 0 || v > 120) return;
    if (!times.includes(v)) setTimes([...times, v]);
    setCustomMin('');
  }, [customMin, times, setTimes]);

  if (!active) return null;

  const presets = [5, 10, 15, 20, 30];
  const modes: { id: IsochroneProfile; label: string; icon: string }[] = [
    { id: 'driving', label: 'Drive', icon: '🚗' },
    { id: 'cycling', label: 'Bike', icon: '🚲' },
    { id: 'walking', label: 'Walk', icon: '🚶' },
  ];

  return (
    <div className="absolute bottom-6 left-1/2 z-controls w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl bg-white/97 p-3 shadow-card-hover backdrop-blur">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-base leading-none">⏱️</span>
          <span className="text-sm font-semibold text-gray-900">Reachable area</span>
        </div>
        <button
          onClick={() => setActive(false)}
          aria-label="Close reachable area"
          className="flex h-6 w-6 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        >
          ✕
        </button>
      </div>

      {/* Travel mode */}
      <div className="mb-2 grid grid-cols-3 gap-1">
        {modes.map((m) => (
          <button
            key={m.id}
            onClick={() => setProfile(m.id)}
            className={`flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
              profile === m.id
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span aria-hidden>{m.icon}</span>
            {m.label}
          </button>
        ))}
      </div>

      {/* Time budgets */}
      <div className="mb-2">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          Time budget (min)
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {Array.from(new Set([...presets, ...times]))
            .sort((a, b) => a - b)
            .map((t) => {
              const selected = times.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleTime(t)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    selected
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={120}
              value={customMin}
              onChange={(e) => setCustomMin(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCustom();
              }}
              placeholder="+"
              className="w-12 rounded-md border border-gray-300 px-1.5 py-1 text-xs focus:border-primary-500 focus:outline-none"
            />
            <button
              onClick={handleAddCustom}
              className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Origin */}
      <div className="mb-2 rounded-lg bg-gray-50 px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 text-xs text-gray-600">
            {origin ? (
              <span>
                Start:{' '}
                <span className="font-medium text-gray-800">
                  {origin.lat.toFixed(4)}, {origin.lng.toFixed(4)}
                </span>
              </span>
            ) : picking ? (
              <span className="text-primary-700">Click the map to set the start point</span>
            ) : (
              <span>No start point</span>
            )}
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <button
            onClick={handleUseMyLocation}
            className="rounded-md bg-white px-2 py-1 text-xs text-gray-700 shadow-card hover:bg-gray-50"
          >
            Use my location
          </button>
          <button
            onClick={() => setPicking(true)}
            className={`rounded-md px-2 py-1 text-xs shadow-card hover:bg-gray-50 ${
              picking ? 'bg-primary-50 text-primary-700' : 'bg-white text-gray-700'
            }`}
          >
            {origin ? 'Change on map' : 'Pick on map'}
          </button>
          {origin && (
            <button
              onClick={() => reset()}
              className="rounded-md bg-white px-2 py-1 text-xs text-gray-700 shadow-card hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Status + legend */}
      {status === 'loading' && (
        <div className="text-xs text-gray-500">Computing reachable area…</div>
      )}
      {status === 'error' && (
        <div className="text-xs text-red-600">
          Couldn’t compute the reachable area. Please try again.
        </div>
      )}
      {status === 'empty' && (
        <div className="text-xs text-gray-600">
          No reachable area returned for this start point.
        </div>
      )}
      {status === 'ok' && drawn.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {drawn.map((d) => (
            <div key={d.time} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: d.color }}
              />
              <span>{d.time} min</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
